/**
 * Hook to sync device orientation between host and guest via Socket.IO.
 * Uses debounced useScreenOrientation - only emits when orientation is stable (FaceTime-style).
 *
 * Both host and guest send so each can rotate the other's video correctly.
 */

import { useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { useScreenOrientation } from '@/shared/hooks/useScreenOrientation';

/** Map useScreenOrientation angle (0,90,180,270) to rotationAngle for VideoStreams compatibility */
function angleToRotationAngle(angle: number): number {
  switch (angle) {
    case 90: return -90;
    case 270: return 90;
    case 180: return 180;
    default: return 0;
  }
}

interface UseOrientationSyncProps {
  socket: Socket | null | React.MutableRefObject<Socket | null>;
  roomId: string | null;
  isHost: boolean;
  enabled: boolean;
  localStreamRef?: React.MutableRefObject<MediaStream | null>;
  onOrientationChange?: (orientation: string, rotationAngle: number) => void;
  facingMode?: 'user' | 'environment';
}

export function useOrientationSync({
  socket,
  roomId,
  isHost,
  enabled,
  onOrientationChange,
  facingMode = 'user'
}: UseOrientationSyncProps) {
  // Both host and guest use the screen orientation API (guest screen rotates freely, same as host)
  const { angle, type, isStable } = useScreenOrientation({
    enabled: enabled && !!roomId,
    debounceMs: 400,
  });
  const rotationAngle = angleToRotationAngle(angle);
  const lastSentAngle = useRef<number | null>(null);
  const lastSentFacingMode = useRef<'user' | 'environment' | null>(null);
  const initialSent = useRef(false);

  const socketInstance = socket && 'current' in socket ? socket.current : socket;

  // Reconnect: re-send on connect
  useEffect(() => {
    if (!enabled || !roomId || !socketInstance) return;
    const handleReconnect = () => {
      initialSent.current = false;
    };
    socketInstance.on('connect', handleReconnect);
    return () => socketInstance.off('connect', handleReconnect);
  }, [enabled, roomId, socketInstance]);

  // Heartbeat every 5 minutes
  useEffect(() => {
    if (!enabled || !roomId || !socketInstance) return;
    const interval = setInterval(() => {
      if (socketInstance.connected) {
        socketInstance.emit('device-orientation', {
          roomId,
          from: socketInstance.id,
          orientation: type,
          rotationAngle,
          facingMode,
          timestamp: Date.now(),
        });
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [enabled, roomId, socketInstance, type, rotationAngle, facingMode]);

  // Emit when orientation is STABLE and changed (FaceTime-style: no rapid flipping)
  useEffect(() => {
    if (!enabled || !roomId || !socketInstance?.connected) return;
    if (!isStable) return; // Only emit when debounce has settled

    const send = () => {
      socketInstance.emit('device-orientation', {
        roomId,
        from: socketInstance.id,
        orientation: type,
        rotationAngle,
        facingMode,
        timestamp: Date.now(),
      });
      lastSentAngle.current = angle;
      lastSentFacingMode.current = facingMode;
      if (onOrientationChange) onOrientationChange(type, rotationAngle);
    };

    if (!initialSent.current) {
      initialSent.current = true;
      setTimeout(send, 1000); // Wait for room join
      return;
    }

    if (lastSentAngle.current === angle && lastSentFacingMode.current === facingMode) return;
    send();
  }, [enabled, roomId, socketInstance, isStable, angle, type, rotationAngle, facingMode, onOrientationChange]);

  return { orientation: type, rotationAngle };
}
